import React, { useState } from 'react';
import {
  IAuditoriaSimCapitalResultado,
  IAuditoriaSimCapitalItem,
} from '../interfaces/IAuditoriaSimCapital';
import { formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaSimCapitalResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// COR-336: nulo = sem base de Capital Social válida para calcular o percentual — não deve ser
// exibido como "0,0000%" (indistinguível de um percentual real calculado sobre uma base válida).
const formatPercentualCapital = (value: number | null | undefined) =>
  value === null || value === undefined ? 'Não determinável' : `${value.toFixed(4)}%`;

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const TipoBadge: React.FC<{ tipo: string }> = ({ tipo }) => {
  if (tipo === 'RECLASSIFICACAO_PASSIVO_PL')
    return <span className='badge badge-light-warning'>Reclas. Passivo → PL</span>;
  return <span className='badge badge-light-danger'>Aporte Sem Lastro</span>;
};

// COR-295: drill-down a nível de lançamento — todo ALERTA/ERRO precisa mostrar não só a
// divergência agregada do mês, mas os lançamentos reais que a compõem.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IAuditoriaSimCapitalItem }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong className='fs-8'>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='table table-sm' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Conta</th>
              <th>Origem</th>
              <th>Histórico</th>
              <th className='text-end'>Valor</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td><code>{lanc.contaContabil}</code></td>
                <td>{lanc.origem === 'CAIXA' ? 'Caixa' : lanc.origem === 'PASSIVO' ? 'Passivo' : 'PL'}</td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{formatCurrency(lanc.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const ItemRow: React.FC<{ item: IAuditoriaSimCapitalItem }> = ({ item }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr>
        <td>
          <button
            className='btn btn-sm btn-icon btn-light me-2'
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} />
          </button>
          <span className='fw-bold'>{item.mesAno}</span>
        </td>
        <td className='text-center'><TipoBadge tipo={item.tipoOcorrencia} /></td>
        <td className='text-end'>{formatCurrency(item.debitoCaixa)}</td>
        <td className='text-end fw-bold'>{formatCurrency(item.creditoPLTotal)}</td>
        <td className='text-end'>{formatCurrency(item.debitoPassivoTotal)}</td>
        <td className='text-end fw-bolder text-danger'>{formatCurrency(item.divergencia)}</td>
        <td className='text-end'>{formatPercentualCapital(item.percentualCapital)}</td>
        <td className='text-end text-danger fw-bold'>{formatCurrency(item.passivoPresumido)}</td>
        <td className='text-center'><StatusBadge status={item.status} /></td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={9} className='bg-light'>
            <div className='p-4'>
              <div className='row g-3 mb-3'>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Débito Caixa (1.01.01.01.01)</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.debitoCaixa)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Crédito Capital Social</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.creditoCapitalSocial)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Crédito Reserva Capital</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.creditoReservaCapital)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Capital Social Acumulado</div>
                    <div className='fw-bolder fs-5'>{formatCurrency(item.capitalSocialAcumulado)}</div>
                  </div>
                </div>
              </div>

              {item.tipoOcorrencia === 'RECLASSIFICACAO_PASSIVO_PL' && (
                <div className='row g-3 mb-3'>
                  <div className='col-md-4'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Déb. Passivo CP (2.01.03)</div>
                      <div className='fw-bolder fs-5'>{formatCurrency(item.debitoPassivoCP)}</div>
                    </div>
                  </div>
                  <div className='col-md-4'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Déb. Passivo LP (2.02.03)</div>
                      <div className='fw-bolder fs-5'>{formatCurrency(item.debitoPassivoLP)}</div>
                    </div>
                  </div>
                  <div className='col-md-4'>
                    <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                      <div className='text-muted fs-8'>Total Déb. Passivo</div>
                      <div className='fw-bolder fs-5 text-warning'>{formatCurrency(item.debitoPassivoTotal)}</div>
                    </div>
                  </div>
                </div>
              )}

              <div className='row g-3 mb-3'>
                <div className='col-md-4'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Divergência (D)</div>
                    <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.divergencia)}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>% do Capital Social</div>
                    <div className='fw-bolder fs-5'>{formatPercentualCapital(item.percentualCapital)}</div>
                  </div>
                </div>
                <div className='col-md-4'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Multa ECD Potencial (Lei 8.218/1991)</div>
                    <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.multaLei8218)}</div>
                  </div>
                </div>
              </div>

              <div className='text-muted fs-9 mb-2'>
                Operações irregulares: {formatCurrency(item.operacoesIrregularesEcd)} × 5% = {formatCurrency(item.multaBruta5Ecd)}
                {' | '}Receita Bruta do Período: {item.receitaBrutaPeriodoEcd != null ? formatCurrency(item.receitaBrutaPeriodoEcd) : 'não disponível'}
                {item.tetoLegal1Ecd != null && <> × 1% = {formatCurrency(item.tetoLegal1Ecd)}</>}
                {' | '}Critério aplicado: {item.criterioAplicadoEcd ?? item.statusEvidenciaEcd}
              </div>

              <div className={`alert p-3 fs-8 ${
                item.status === 'ERRO'   ? 'alert-light-danger border border-danger' :
                                           'alert-light-warning border border-warning'
              }`}>
                {item.mensagemDiagnostico}
              </div>

              {item.mensagemDirecionamento && (
                <div className='alert alert-light border border-secondary p-2 fs-9 mt-2'>
                  <strong>Direcionamento:</strong> {item.mensagemDirecionamento}
                </div>
              )}

              <BlocoLancamentosRelacionados item={item} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaSimCapitalDetalhe: React.FC<Props> = ({ resultado }) => {
  if (!resultado) return null;

  return (
    <div className='card card-custom card-stretch mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-4 mb-1'>
            Detalhamento — Auditoria de Simetria em Contas de Capital
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.itens.length} ocorrência(s) ·{' '}
            {resultado.totalErros} erro(s) · {resultado.totalAlertas} alerta(s)
            {resultado.passivoTotal > 0 && (
              <> · Passivo:{' '}
                <strong className='text-danger'>
                  {resultado.passivoTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </strong>
              </>
            )}
          </span>
        </h3>
      </div>

      <div className='card-body pt-3'>
        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Nenhuma anomalia em contas de capital detectada. Situação regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Mês/Ano</th>
                  <th className='text-center'>Tipo</th>
                  <th className='text-end'>Deb. Caixa</th>
                  <th className='text-end'>Créd. PL Total</th>
                  <th className='text-end'>Deb. Passivo</th>
                  <th className='text-end'>Divergência</th>
                  <th className='text-end'>% Capital</th>
                  <th className='text-end'>Passivo Presumido</th>
                  <th className='text-center'>Status</th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemRow key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {resultado.fundamentacaoLegal && (
          <div className='alert alert-light border border-secondary p-3 fs-9 mt-4'>
            <strong>Fundamentação Legal:</strong> {resultado.fundamentacaoLegal}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaSimCapitalDetalhe;
